<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref, shallowRef, watch } from 'vue';
import type { Id } from '@vp/shared-types';
import { live, ui } from '../editor/bridge';
import { buildSrcdoc, readBridgeMessage, vSandbox } from '../editor/sandbox';
import { fromSandbox, moduleHasKeyboard, modulesState, moduleView, onViewPost, sandboxDoc, sendViewMessage, typeContribution } from '../modules';
import { t } from '../i18n';
import { logger } from '../platform';
import { INK_FILTER } from '@vp/render';
import { canvasDark } from '../theme';

/**
 * Live views of render mode C objects (§7.3): sandboxed iframes positioned over the
 * canvas, synced with camera position and zoom, limited in number. A view becomes
 * interactive after a double click; otherwise clicks go to the canvas.
 * API 4.6: at most MAX_ACTIVE live views of one module; a view keeps the keyboard only when its
 * module has the `keyboard` permission (a badge says so; Escape gives the keyboard back).
 */
const MAX_ACTIVE = 8;
const MIN_PX = 80;

interface View {
  id: Id;
  moduleId: string;
  type: string;
  html: string;
}

const views = shallowRef<View[]>([]);
const tick = ref(0);
const root = ref<HTMLDivElement | null>(null);
function frameOf(id: Id): HTMLIFrameElement | null {
  return (root.value?.querySelector('iframe[data-oid="' + CSS.escape(id) + '"]') as HTMLIFrameElement | null) ?? null;
}
/**
 * Room a view asks for outside its object (a toolbar): screen px, reported by the view with
 * vp.reserve() of its bridge. The active view gets it above the object — below it when there is no room
 * up there (under the top bars) — so the toolbar never covers the content.
 */
const chrome = reactive(new Map<Id, { top: number; width: number; below: number }>());
/**
 * `view.fit: scale` (API 2.0): how much the content of a view is scaled down to fit its object.
 * Adjusted from the content size the bridge reports; `tries` stops a content that keeps reflowing.
 */
const fitScale = reactive(new Map<Id, number>());
const fitTries = new Map<Id, { key: string; n: number }>();
const MIN_FIT_SCALE = 0.1;
const TOP_CLEAR = 140;
/**
 * Room outside the object: toolbar height (px) and side, the width of the whole view (the toolbar
 * may be wider than the object) and, while a menu of the view is open, the room below the object it
 * needs (`below`) — popups of a view are never cut by the object or by an enclosing frame.
 */
function room(v: View): { px: number; side: 'top' | 'bottom'; wide: number; padX: number; below: number } {
  const c = ui.activeView === v.id ? chrome.get(v.id) : undefined;
  const box = live.editor?.screenBox(v.id);
  if (!c || !c.top || !box) return { px: 0, side: 'top', wide: box?.w ?? 0, padX: 0, below: 0 };
  const wide = Math.max(box.w, c.width);
  return { px: c.top, side: box.y - c.top < TOP_CLEAR ? 'bottom' : 'top', wide, padX: (wide - box.w) / 2, below: c.below };
}
const htmlCache = new Map<string, string>();
let raf = 0;
let unsubs: (() => void)[] = [];
let camUnsub: (() => void) | null = null;
let camFor: unknown = null;

function schedule(): void {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = 0;
    void update();
    tick.value++;
  });
}

async function update(): Promise<void> {
  const ed = live.editor;
  const r = live.renderer;
  if (!ed || !r) {
    views.value = [];
    return;
  }
  if (camFor !== r) {
    // the renderer is created after this component mounts: follow its camera lazily
    camUnsub?.();
    camUnsub = r.camera.onChange(schedule);
    camFor = r;
  }
  const cam = r.camera;
  const rect = cam.visibleRect();
  const candidates: { id: Id; type: string; moduleId: string; d: number }[] = [];
  for (const id of ed.scene.query(rect)) {
    const o = ed.doc.get(id);
    if (!o || o.type.startsWith('core:')) continue;
    const info = typeContribution(o.type);
    if (!info || info.contribution.renderMode !== 'C' || ed.scene.displayCtx.modules?.primitives(o, ed.doc.rev(o.id)).status === 'unavailable') continue;
    if (o.w * cam.zoom < MIN_PX || o.h * cam.zoom < MIN_PX / 2) continue;
    const d = Math.hypot(o.x + o.w / 2 - cam.cx, o.y + o.h / 2 - cam.cy);
    candidates.push({ id, type: o.type, moduleId: info.moduleId, d });
  }
  candidates.sort((a, b) => a.d - b.d);
  // the nearest MAX_ACTIVE of each module
  const perModule = new Map<string, number>();
  const chosen = candidates.filter((c) => {
    const n = perModule.get(c.moduleId) ?? 0;
    perModule.set(c.moduleId, n + 1);
    return n < MAX_ACTIVE;
  });
  const next: View[] = [];
  for (const c of chosen) {
    const existing = views.value.find((v) => v.id === c.id);
    if (existing) {
      next.push(existing);
      continue;
    }
    const key = `${c.moduleId}@${modulesState.rev}:${c.type}:${c.id}`;
    let html = htmlCache.get(key);
    if (!html) {
      try {
        html = buildSrcdoc(await moduleView(c.type, 'object', c.id));
        htmlCache.set(key, html);
      } catch (e) {
        logger.warn('module.view.failed', { type: c.type, code: (e as { code?: string }).code ?? 'unknown' });
        continue;
      }
    }
    next.push({ id: c.id, moduleId: c.moduleId, type: c.type, html });
  }
  for (const v of views.value) if (!next.some((n) => n.id === v.id)) sendViewMessage(v.moduleId, v.id, { type: 'closed' });
  const same = next.length === views.value.length && next.every((v, i) => v === views.value[i]);
  if (!same) views.value = next;
}

function style(v: View): Record<string, string> {
  void tick.value;
  void ui.docRev;
  const box = live.editor?.screenBox(v.id);
  const o = live.editor?.doc.get(v.id);
  if (!box || !o) return { display: 'none' };
  const { px, side, wide, padX, below } = room(v);
  const st: Record<string, string> = {
    left: box.x - padX + 'px',
    top: box.y - (side === 'top' ? px : 0) + 'px',
    width: wide + 'px',
    height: box.h + px + below + 'px',
    // the object turns about its own center, the toolbar room turns with it
    transformOrigin: `50% ${(side === 'top' ? px : 0) + box.h / 2}px`,
    transform: `rotate(${box.rotation}rad)`,
  };
  // live views are clipped by enclosing frames just like canvas content (not while editing:
  // the toolbar outside the object must stay in reach)
  const clip = px ? null : live.editor?.scene.clipOf(o);
  const cam = live.renderer?.camera;
  if (clip && cam) {
    const a = cam.worldToScreen(clip.x, clip.y);
    const b = cam.worldToScreen(clip.x + clip.w, clip.y + clip.h);
    const top = Math.max(0, a.y - box.y);
    const left = Math.max(0, a.x - box.x);
    const right = Math.max(0, box.x + box.w - b.x);
    const bottom = Math.max(0, box.y + box.h - b.y);
    if (top + bottom >= box.h || left + right >= box.w) return { display: 'none' };
    st.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`;
  }
  if (px && !below) {
    // only the toolbar strip and the object take clicks: the transparent corners beside a wide
    // toolbar let clicks through to the canvas
    const y0 = side === 'top' ? px : 0;
    const x0 = padX;
    const x1 = padX + box.w;
    const y1 = y0 + box.h;
    const bar = side === 'top' ? [[0, 0], [wide, 0], [wide, px], [x1, px], [x1, y1], [x0, y1], [x0, px], [0, px]] : [[x0, 0], [x1, 0], [x1, box.h], [wide, box.h], [wide, box.h + px], [0, box.h + px], [0, box.h], [x0, box.h]];
    st.clipPath = `polygon(${bar.map(([x, y]) => x + 'px ' + y + 'px').join(',')})`;
  }
  return st;
}

/** The frame of an active view goes around the object only, not around its toolbar. */
function ringStyle(v: View): Record<string, string> {
  void tick.value;
  const box = live.editor?.screenBox(v.id);
  const { px, side, padX } = room(v);
  if (!box || ui.activeView !== v.id) return { display: 'none' };
  return { left: padX + 'px', width: box.w + 'px', top: (side === 'top' ? px : 0) + 'px', height: box.h + 'px' };
}

function frameStyle(v: View): Record<string, string> {
  void tick.value;
  const o = live.editor?.doc.get(v.id);
  const z = live.renderer?.camera.zoom ?? 1;
  if (!o) return {};
  const { px, wide, below } = room(v);
  const box = live.editor?.screenBox(v.id);
  // view.fit: scale — the document is laid out larger and shown smaller, so all of it fits
  const k = fitScale.get(v.id) ?? 1;
  const zk = z * k;
  return {
    // the view is as wide as its toolbar needs (centered on the object), in object units
    width: (box && wide > box.w ? wide / zk : o.w / k) + 'px',
    height: o.h / k + (px + below) / zk + 'px',
    transform: `scale(${zk})`,
    pointerEvents: ui.activeView === v.id && !ui.readOnly ? 'auto' : 'none',
    // the dark canvas shows colors inverted: the view follows so the object looks the same
    ...(canvasDark.value ? { filter: INK_FILTER } : {}),
  };
}


/** Content size of a view (object units) → the object grows to it, or the content scales to fit. */
function onContentSize(v: View, size: { w: number; h: number }): void {
  const info = typeContribution(v.type)?.contribution.view;
  const o = live.editor?.doc.get(v.id);
  if (!info?.fit || info.fit === 'none' || !o || size.w <= 0 || size.h <= 0) return;
  if (info.fit === 'grow') {
    const clamp = (x: number, lo?: number, hi?: number) => Math.min(hi ?? 100000, Math.max(lo ?? 20, x));
    live.editor?.fitToView(v.id, {
      h: clamp(size.h, info.min?.h, info.max?.h),
      ...(info.axis === 'both' ? { w: clamp(size.w, info.min?.w, info.max?.w) } : {}),
    });
    return;
  }
  // scale: the document is laid out at (w/k, h/k); the content must fit that box
  const k = fitScale.get(v.id) ?? 1;
  const want = Math.max(MIN_FIT_SCALE, Math.min(1, k * Math.min((o.w / k) / size.w, (o.h / k) / size.h)));
  if (Math.abs(want - k) / k < 0.02) return;
  const key = o.w + 'x' + o.h;
  const t = fitTries.get(v.id);
  const n = t && t.key === key ? t.n + 1 : 1;
  fitTries.set(v.id, { key, n });
  if (n > 8) return;
  fitScale.set(v.id, want);
}

function onMessage(e: MessageEvent): void {
  const bridge = readBridgeMessage(e.data);
  if (bridge) {
    const v = views.value.find((x) => frameOf(x.id)?.contentWindow === e.source);
    if (!v) return;
    if (bridge.chrome) chrome.set(v.id, bridge.chrome);
    if (bridge.size) onContentSize(v, bridge.size);
    if (bridge.escape) giveBack(v.id);
    return;
  }
  const d = e.data as { vpView?: number; data?: unknown } | null;
  if (!d || d.vpView !== 1) return;
  for (const v of views.value) {
    const f = frameOf(v.id);
    if (f && e.source === f.contentWindow) {
      if (!fromSandbox(v.moduleId, d.data)) return;
      if (ui.readOnly && (d.data as { type?: string })?.type !== 'ready') return;
      // a view may end its own editing (Esc, a Done button)
      if ((d.data as { type?: string })?.type === 'deactivate') giveBack(v.id);
      sendViewMessage(v.moduleId, v.id, d.data);
      return;
    }
  }
}

/** The view's editing ends and the keyboard goes back to the canvas. */
function giveBack(id: Id): void {
  if (ui.activeView === id) ui.activeView = null;
  if (keyFocus.value === id) keyFocus.value = null;
  (document.querySelector('.canvas-host') as HTMLElement | null)?.focus({ preventScroll: true });
}

/**
 * The view that has the keyboard now (API 4.6). A view of a module without `keyboard` never keeps it:
 * focus that lands in it (a click inside) goes back to the canvas at once.
 */
const keyFocus = ref<Id | null>(null);
function checkFocus(): void {
  const a = document.activeElement;
  const v = a instanceof HTMLIFrameElement ? views.value.find((x) => frameOf(x.id) === a) : undefined;
  if (v && !moduleHasKeyboard(v.moduleId)) {
    keyFocus.value = null;
    (document.querySelector('.canvas-host') as HTMLElement | null)?.focus({ preventScroll: true });
    return;
  }
  keyFocus.value = v?.id ?? null;
}
const onBlur = () => setTimeout(checkFocus, 0);

onMounted(() => {
  window.addEventListener('message', onMessage);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', checkFocus);
  unsubs.push(
    onViewPost((_m, objectId, data) => {
      frameOf(objectId)?.contentWindow?.postMessage({ vpView: 1, data }, '*');
    }),
  );
  schedule();
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  window.removeEventListener('blur', onBlur);
  window.removeEventListener('focus', checkFocus);
  for (const u of unsubs) u();
  if (raf) cancelAnimationFrame(raf);
});

watch(() => [ui.zoom, ui.docRev, modulesState.rev, ui.selection], schedule);
/**
 * Tell views when they become interactive and when they stop (double click / click elsewhere):
 * the frame gets the focus first, so the view can put the caret into its own document.
 */
/** Tell an active view its zoom and the room it has outside the object (bridge channel → vp.viewport). */
function sendViewport(): void {
  const id = ui.activeView;
  const v = id ? views.value.find((x) => x.id === id) : undefined;
  if (!id || !v) return;
  const { px, side, padX, below } = room(v);
  frameOf(id)?.contentWindow?.postMessage({ vpBridge: 1, viewport: { zoom: live.renderer?.camera.zoom ?? 1, space: px, side, padX, below } }, '*');
}
watch(() => [ui.zoom, ui.activeView, ui.docRev, ...[...chrome.values()].map((c) => c.top + ':' + c.width + ':' + c.below)], () => requestAnimationFrame(sendViewport));

watch(
  () => ui.activeView,
  (now, before) => {
    if (keyFocus.value && keyFocus.value !== now) keyFocus.value = null;
    if (before) {
      const v = views.value.find((x) => x.id === before);
      if (v) {
        frameOf(before)?.contentWindow?.postMessage({ vpView: 1, data: { type: 'deactivated' } }, '*');
        sendViewMessage(v.moduleId, before, { type: 'deactivated' });
      }
    }
    if (now) {
      const v = views.value.find((x) => x.id === now);
      const f = frameOf(now);
      if (v && f && !ui.readOnly) {
        // the keyboard goes into the view only with the module's `keyboard` permission
        if (moduleHasKeyboard(v.moduleId)) f.focus();
        f.contentWindow?.postMessage({ vpView: 1, data: { type: 'activated' } }, '*');
        requestAnimationFrame(sendViewport);
        sendViewMessage(v.moduleId, now, { type: 'activated' });
      }
    }
  },
);
onBeforeUnmount(() => camUnsub?.());

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') ui.activeView = null;
}
</script>

<template>
  <div ref="root" class="module-views" @keydown="onKey">
    <div v-for="v in views" :key="v.id" class="view" :class="{ active: ui.activeView === v.id }" :style="style(v)">
      <span class="ring" :style="ringStyle(v)" aria-hidden="true"></span>
      <span v-if="keyFocus === v.id" class="key-badge pill" :style="ringStyle(v)" data-key-badge>{{ t('modules.keyboardHere') }}</span>
      <iframe :data-oid="v.id" :data-module="v.moduleId" v-sandbox="sandboxDoc(v.moduleId, v.html)" referrerpolicy="no-referrer" :title="v.type" :style="frameStyle(v)" tabindex="-1"></iframe>
    </div>
  </div>
</template>

<style scoped>
.module-views {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 3;
}
.view {
  position: absolute;
  transform-origin: center center;
  overflow: hidden;
  border-radius: 4px;
}
.view.active {
  overflow: visible;
}
.key-badge {
  position: absolute;
  z-index: 3;
  height: auto !important;
  width: auto !important;
  transform: translateY(calc(-100% - 6px));
  padding: 2px 10px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  background: var(--panel);
  color: var(--text);
  box-shadow: var(--shadow);
}
.ring {
  position: absolute;
  pointer-events: none;
  border-radius: 4px;
  box-shadow: 0 0 0 2px var(--accent);
  z-index: 2;
}
iframe {
  border: none;
  transform-origin: 0 0;
  display: block;
  /* the view paints its own background: an idle view may stay see-through over its canvas drawing */
  background: transparent;
  color-scheme: normal;
}
</style>
